import { useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import type { RouterHistory } from '@tanstack/react-router';
import { I18nextProvider } from 'react-i18next';
import * as Sentry from '@sentry/react';
import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import { i18n } from '@/shared/i18n/config';
import { AppToast } from '@/shared/components/ui/AppToast/AppToast';
import { ErrorFallbackPage } from './ErrorFallbackPage';
import { createAppRouter } from './router';
import { AuthBootstrap } from './AuthBootstrap';

function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 5 * 60 * 1000, // 5 minutes
        gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
        retry: (failureCount, error) => {
          // Don't retry on 4xx errors
          const apiError = error as { status?: number };
          if (apiError?.status && apiError.status >= 400 && apiError.status < 500) {
            return false;
          }
          return failureCount < 2;
        },
        refetchOnWindowFocus: true,
      },
      mutations: {
        retry: 0,
      },
    },
  });
}

export interface ProvidersProps {
  /**
   * Optional router history. Standalone omits it (browser history); the Module
   * Federation remote passes a memory history so it never touches the host URL.
   */
  history?: RouterHistory;
}

/**
 * Application provider tree.
 * Order (outermost -> innermost):
 * 1. Sentry ErrorBoundary -- catches errors from all child providers
 * 2. QueryClientProvider -- wraps Router so loaders can use queryClient
 * 3. I18nextProvider -- internationalization
 * 4. RouterProvider -- TanStack Router
 *
 * NO ThemeProvider -- CSS custom properties handle theming.
 */
export function Providers({ history }: ProvidersProps = {}) {
  // Fresh QueryClient + router per mount, so a remount (e.g. embedded org
  // switch) starts with an empty cache rather than reusing another org's data.
  const [queryClient] = useState(createQueryClient);
  const [router] = useState(() => createAppRouter(queryClient, { history }));

  return (
    <Sentry.ErrorBoundary fallback={<ErrorFallbackPage />}>
      <QueryClientProvider client={queryClient}>
        <I18nextProvider i18n={i18n}>
          <PostHogProvider client={posthog}>
            <AuthBootstrap />
            <RouterProvider router={router} />
            {/* Global toast renderer — lives in Providers so both the
                standalone app and the module-federation remote (which mounts
                Providers in its own React root) get it. */}
            <AppToast />
          </PostHogProvider>
        </I18nextProvider>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  );
}
